import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AppBar from "../../../common/AppBar";
import { buildPermissionContext } from "../../../util/permissions";

/**
 * PreviewAppBar - Simplified AppBar for preview pages
 * Shows only basic navigation and document info
 * No action buttons (Submit/Reject/Approve/Release)
 */
const PreviewAppBar = ({
  // Document Info
  type,
  partNumber,
  revision,
  revisionId,
  partId,
  description,

  // User Info
  userName = localStorage.getItem("user_name"),

  // AppBar Options
  showTablesDropdown = true,
  showBlackTitle = true,
  showBookmark = true,
  navigateTwoPagesBack = true,

  // Custom Buttons (if needed)
  customButtons = null,
  customButtonsBeforeTitle = null,

  // Optional handlers
  onVersionHistory = null,
  onExportClick = null,
  onClose = null,
}) => {
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canExport = permissionCtx.permissions?.["Import/Export"] === true;

  /**
   * Handle Version History click
   */
  const handleVersionHistory = () => {
    if (onVersionHistory) {
      onVersionHistory();
    }
  };

  /**
   * Handle Back to Document click
   * Returns to the full document page from preview
   */
  const handleBackToDocument = () => {
    if (partId && revisionId) {
      navigate(`/document/${partId}/${revisionId}`);
    }
  };

  // Generate title
  const title = `${type || ""} ${partNumber || ""}, Rev ${revision || ""}`;

  // Generate subtitle with spacing
  const subTitleAboveTitle = (
    <span style={{ marginBottom: "10px", display: "inline-block" }}>
      {userName} is Previewing
    </span>
  );

  // Default buttons for preview mode
  const defaultButtons =
    customButtons ||
    (onClose
      ? [
          {
            label: "Close Preview",
            onClick: onClose,
          },
        ]
      : [
          {
            label: "Back to Document",
            onClick: handleBackToDocument,
          },
        ]);

  // No buttons before title in preview mode by default
  const defaultButtonsBeforeTitle = customButtonsBeforeTitle || [];

  return (
    <AppBar
      title={title}
      showTablesDropdown={false}
      showProfileButton={false}
      showBlackTitle={showBlackTitle}
      buttons={[
        {
          label: (
            <svg
              width="38"
              height="36"
              viewBox="0 0 38 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.5 9.9C11.6326 9.9 11.7598 9.95268 11.8536 10.0464C11.9473 10.1402 12 10.2674 12 10.4V12.9C12 13.1652 12.1054 13.4196 12.2929 13.6071C12.4804 13.7946 12.7348 13.9 13 13.9H25C25.2652 13.9 25.5196 13.7946 25.7071 13.6071C25.8946 13.4196 26 13.1652 26 12.9V10.4C26 10.2674 26.0527 10.1402 26.1464 10.0464C26.2402 9.95268 26.3674 9.9 26.5 9.9C26.6326 9.9 26.7598 9.95268 26.8536 10.0464C26.9473 10.1402 27 10.2674 27 10.4V12.9C27 13.4304 26.7893 13.9391 26.4142 14.3142C26.0391 14.6893 25.5304 14.9 25 14.9H13C12.4696 14.9 11.9609 14.6893 11.5858 14.3142C11.2107 13.9391 11 13.4304 11 12.9V10.4C11 10.2674 11.0527 10.1402 11.1464 10.0464C11.2402 9.95268 11.3674 9.9 11.5 9.9Z"
                fill="#6C757D"
              />
              <path
                d="M18.646 11.854C18.6924 11.9006 18.7476 11.9375 18.8084 11.9627C18.8691 11.9879 18.9342 12.0009 19 12.0009C19.0658 12.0009 19.1309 11.9879 19.1916 11.9627C19.2524 11.9375 19.3076 11.9006 19.354 11.854L22.354 8.854C22.4479 8.76011 22.5006 8.63278 22.5006 8.5C22.5006 8.36722 22.4479 8.23989 22.354 8.146C22.2601 8.05211 22.1328 7.99937 22 7.99937C21.8672 7.99937 21.7399 8.05211 21.646 8.146L19.5 10.293V1.5C19.5 1.36739 19.4473 1.24021 19.3536 1.14645C19.2598 1.05268 19.1326 1 19 1C18.8674 1 18.7402 1.05268 18.6464 1.14645C18.5527 1.24021 18.5 1.36739 18.5 1.5V10.293L16.354 8.146C16.2601 8.05211 16.1328 7.99937 16 7.99937C15.8672 7.99937 15.7399 8.05211 15.646 8.146C15.5521 8.23989 15.4994 8.36722 15.4994 8.5C15.4994 8.63278 15.5521 8.76011 15.646 8.854L18.646 11.854Z"
                fill="#6C757D"
              />
              <path
                d="M6.94434 30.0034V31H2.05029V30.0034H6.94434ZM2.29785 21.7578V31H1.07275V21.7578H2.29785ZM6.29688 25.7314V26.728H2.05029V25.7314H6.29688ZM6.88086 21.7578V22.7607H2.05029V21.7578H6.88086ZM9.07715 24.1318L10.5815 26.6328L12.105 24.1318H13.4824L11.2354 27.5215L13.5522 31H12.1938L10.6069 28.4229L9.02002 31H7.65527L9.96582 27.5215L7.7251 24.1318H9.07715ZM15.9072 25.4521V33.6406H14.7266V24.1318H15.8057L15.9072 25.4521ZM20.5347 27.5088V27.6421C20.5347 28.1414 20.4754 28.6048 20.3569 29.0322C20.2384 29.4554 20.0649 29.8236 19.8364 30.1367C19.6121 30.4499 19.335 30.6932 19.0049 30.8667C18.6748 31.0402 18.2961 31.127 17.8687 31.127C17.4328 31.127 17.0477 31.055 16.7134 30.9111C16.3791 30.7673 16.0955 30.5578 15.8628 30.2827C15.63 30.0076 15.4438 29.6776 15.3042 29.2925C15.1688 28.9074 15.0757 28.4736 15.0249 27.9912V27.2803C15.0757 26.7725 15.1709 26.3175 15.3105 25.9155C15.4502 25.5135 15.6343 25.1707 15.8628 24.8872C16.0955 24.5994 16.377 24.3815 16.707 24.2334C17.0371 24.0811 17.418 24.0049 17.8496 24.0049C18.2812 24.0049 18.6642 24.0895 18.9985 24.2588C19.3328 24.4238 19.6143 24.6608 19.8428 24.9697C20.0713 25.2786 20.2427 25.6489 20.3569 26.0806C20.4754 26.508 20.5347 26.984 20.5347 27.5088ZM19.354 27.6421V27.5088C19.354 27.166 19.318 26.8444 19.2461 26.5439C19.1742 26.2393 19.062 25.9727 18.9097 25.7441C18.7616 25.5114 18.5711 25.3294 18.3384 25.1982C18.1056 25.0628 17.8285 24.9951 17.5068 24.9951C17.2106 24.9951 16.9525 25.0459 16.7324 25.1475C16.5166 25.249 16.3325 25.3866 16.1802 25.5601C16.0278 25.7293 15.903 25.924 15.8057 26.144C15.7126 26.3599 15.6427 26.5841 15.5962 26.8169V28.4609C15.6808 28.7572 15.7993 29.0365 15.9517 29.2988C16.104 29.557 16.3071 29.7664 16.561 29.9272C16.8149 30.0838 17.1344 30.1621 17.5195 30.1621C17.8369 30.1621 18.1099 30.0965 18.3384 29.9653C18.5711 29.8299 18.7616 29.6458 18.9097 29.4131C19.062 29.1803 19.1742 28.9137 19.2461 28.6133C19.318 28.3086 19.354 27.9849 19.354 27.6421ZM21.7217 27.6421V27.4961C21.7217 27.001 21.7936 26.5418 21.9375 26.1187C22.0814 25.6912 22.2887 25.321 22.5596 25.0078C22.8304 24.6904 23.1584 24.445 23.5435 24.2715C23.9285 24.0938 24.3602 24.0049 24.8384 24.0049C25.3208 24.0049 25.7546 24.0938 26.1396 24.2715C26.529 24.445 26.859 24.6904 27.1299 25.0078C27.4049 25.321 27.6144 25.6912 27.7583 26.1187C27.9022 26.5418 27.9741 27.001 27.9741 27.4961V27.6421C27.9741 28.1372 27.9022 28.5964 27.7583 29.0195C27.6144 29.4427 27.4049 29.813 27.1299 30.1304C26.859 30.4435 26.5311 30.689 26.146 30.8667C25.7651 31.0402 25.3335 31.127 24.8511 31.127C24.3687 31.127 23.9349 31.0402 23.5498 30.8667C23.1647 30.689 22.8346 30.4435 22.5596 30.1304C22.2887 29.813 22.0814 29.4427 21.9375 29.0195C21.7936 28.5964 21.7217 28.1372 21.7217 27.6421ZM22.896 27.4961V27.6421C22.896 27.9849 22.9362 28.3086 23.0166 28.6133C23.097 28.9137 23.2176 29.1803 23.3784 29.4131C23.5435 29.6458 23.7487 29.8299 23.9941 29.9653C24.2396 30.0965 24.5252 30.1621 24.8511 30.1621C25.1727 30.1621 25.4541 30.0965 25.6953 29.9653C25.9408 29.8299 26.1439 29.6458 26.3047 29.4131C26.4655 29.1803 26.5861 28.9137 26.6665 28.6133C26.7511 28.3086 26.7935 27.9849 26.7935 27.6421V27.4961C26.7935 27.1576 26.7511 26.8381 26.6665 26.5376C26.5861 26.2329 26.4634 25.9642 26.2983 25.7314C26.1375 25.4945 25.9344 25.3083 25.689 25.1729C25.4478 25.0374 25.1642 24.9697 24.8384 24.9697C24.5168 24.9697 24.2332 25.0374 23.9878 25.1729C23.7466 25.3083 23.5435 25.4945 23.3784 25.7314C23.2176 25.9642 23.097 26.2329 23.0166 26.5376C22.9362 26.8381 22.896 27.1576 22.896 27.4961ZM30.6211 25.2109V31H29.4468V24.1318H30.5894L30.6211 25.2109ZM32.7666 24.0938L32.7603 25.1855C32.6629 25.1644 32.5698 25.1517 32.481 25.1475C32.3963 25.139 32.299 25.1348 32.189 25.1348C31.9181 25.1348 31.679 25.1771 31.4717 25.2617C31.2643 25.3464 31.0887 25.4648 30.9448 25.6172C30.8009 25.7695 30.6867 25.9515 30.6021 26.1631C30.5216 26.3704 30.4688 26.599 30.4434 26.8486L30.1133 27.0391C30.1133 26.6243 30.1535 26.235 30.2339 25.8711C30.3185 25.5072 30.4476 25.1855 30.6211 24.9062C30.7946 24.6227 31.0146 24.4027 31.2812 24.2461C31.5521 24.0853 31.8737 24.0049 32.2461 24.0049C32.3307 24.0049 32.4281 24.0155 32.5381 24.0366C32.6481 24.0535 32.7243 24.0726 32.7666 24.0938ZM37.0449 24.1318V25.0332H33.3315V24.1318H37.0449ZM34.5884 22.4624H35.7627V29.2988C35.7627 29.5316 35.7987 29.7072 35.8706 29.8257C35.9425 29.9442 36.0356 30.0225 36.1499 30.0605C36.2642 30.0986 36.3869 30.1177 36.5181 30.1177C36.6154 30.1177 36.717 30.1092 36.8228 30.0923C36.9328 30.0711 37.0153 30.0542 37.0703 30.0415L37.0767 31C36.9836 31.0296 36.8608 31.0571 36.7085 31.0825C36.5604 31.1121 36.3805 31.127 36.1689 31.127C35.8812 31.127 35.6167 31.0698 35.3755 30.9556C35.1343 30.8413 34.9417 30.6509 34.7979 30.3843C34.6582 30.1134 34.5884 29.7495 34.5884 29.2925V22.4624Z"
                fill="#6C757D"
              />
            </svg>
          ),
          onClick: canExport ? onExportClick || (() => {}) : undefined,
          disabled: !canExport,
          title: canExport
            ? "Export Document"
            : "You do not have permission to export documents",
        },
      ]}
      buttonsBeforeTitle={[]}
      navigateTwoPagesBack={navigateTwoPagesBack}
      subTitleAboveTitle={subTitleAboveTitle}
      versionHistoryHandler={handleVersionHistory}
      showEyeIcon={false}
      onBack={onClose}
    />
  );
};

export default PreviewAppBar;
